import fetch from 'cross-fetch';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost';
const AUTH_API = process.env.AUTH_API || 'auth'; // 本物
// const AUTH_API = process.env.AUTH_API || 'auth2'; // ダミー ログイン失敗
// const AUTH_API = process.env.AUTH_API || 'auth3'; // ダミー ログイン成功

export interface TagsResult {
  [key: string]: string;
}

export interface LoginResult {
  accessToken: string;
}

export interface UploadFile {
  id: number;
  fileName: string;
  fileSize: number;
}

export interface UserInfo {
  id: string;
  name: string;
}

export interface UploadFileForHighlight {
  id: number;
  fileName: string;
  fileSize: number;
}

export interface UploadFileForSearch {
  id: number;
  fileName: string;
  fileSize: number;
}

export interface UploadFileForRiskJudge {
  id: number;
  fileName: string;
  fileSize: number;
}

export interface RiskJudge {
  mainDoc: string;
  logics: {
    title: string;
    tempDoc: string;
    logicColor: string;
    numberTitle: string;
    judges: {
      name: string;
      judgeType: string;
      specificId: {
        id: string;
        linkName: string;
        bgcolor: string;
      }[];
    }[];
  }[];
}

export interface Comparison {
  documents: {
    fileName: string;
    tags: { [key: string]: string[] };
    diff: { [key: string]: string[] };
  }[];
  tags: { [key: string]: string };
}

export interface ComparisonForHighlight {
  documents: {
    fileName: string;
    tags: { [key: string]: string[] };
    diff: { [key: string]: string[] };
  }[];
  tags: { [key: string]: string };
}

export interface ComparisonSearch {
  documents: {
    fileName: string;
    mainDoc: string;
  }[];
  tags: { [key: string]: string };
}

export interface ApplyContent {
  fileName: string;
  orgTagId: number;
  newTagId: number;
  position: number; // 比較対象のファイル毎に振られる数値
  order: number; // 同ファイル内、同カテゴリ内での位置
  text: string;
}

export interface SearchConditions {
  name: string;
  status: number;
  orgTagId: number;
  newTagId: number;
  limit: number;
  offset: number;
  page: number;
}

export interface SearchResult {
  totalCount: number;
  returnCount: number;
  errorSentences: {
    id: string;
    name: string;
    status: number;
    fileName: string;
    orgTagId: string;
    newTagId: string;
    sentence: string;
    created_at: string;
  }[];
}

export interface DownloadConditions {
  name: string;
  status: number;
  orgTagId: number;
  newTagId: number;
}

export type UpdateConditions = {
  id: number;
  newTagId: number | null;
  status: number | null;
};

interface TagsResponse {
  messages: string[];
  status: number;
  result?: TagsResult;
  status_code: number | 0;
}

interface LoginResponse {
  status_code: number;
  error: string;
  description: string;
  access_token: string;
}

interface UserInfoResponse {
  status: number;
  result?: UserInfo;
  status_code: number;
}

interface UploadResponse {
  messages: string[];
  status: number;
  result?: UploadFile;
  status_code: number | 0;
}

interface UploadForHighlightResponse {
  messages: string[];
  status: number;
  result?: UploadFileForHighlight;
  status_code: number | 0;
}

interface UploadForSearchResponse {
  messages: string[];
  status: number;
  result?: UploadFileForSearch;
  status_code: number | 0;
}

interface UploadForRiskJudgeResponse {
  messages: string[];
  status: number;
  result?: UploadFileForRiskJudge;
  status_code: number | 0;
}

interface RiskJudgeResponse {
  messages: string[];
  status: number;
  result?: RiskJudge;
  status_code: number | 0;
}

interface ComparisonResponse {
  messages: string[];
  status: number;
  result?: Comparison;
  status_code: number | 0;
}

interface ComparisonForHighlightResponse {
  messages: string[];
  status: number;
  result?: ComparisonForHighlight;
  status_code: number | 0;
}

interface ComparisonForHighlightSaveResponse {
  status: number;
  status_code: number | 0;
}

interface ComparisonSearchResponse {
  messages: string[];
  status: number;
  result?: ComparisonSearch;
  status_code: number | 0;
}

interface ApplyResponse {
  messages: string[];
  status: number;
  result?: ApplyContent;
  status_code: number | 0;
}

interface SearchResponse {
  messages: string[];
  status: number;
  result?: SearchResult;
  status_code: number | 0;
}

interface UpdateResponse {
  messages: string[];
  status: number;
  result?: null;
  status_code: number | 0;
}

interface DeleteResponse {
  messages: string[];
  status: number;
  result?: null;
  status_code: number | 0;
}

// クッキーからAPI認証用トークンを取得
function getAccessToken() {
  const cookies = document.cookie;
  let authKey = '';
  if (cookies.includes('access_token')) {
    cookies.split(';').forEach((cookie) => {
      if (cookie.includes('access_token')) {
        authKey = cookie.split('=')[1];
      }
    });
  }
  return authKey;
}

export async function tags() {
  const url = `${BASE_URL}/contract/tags?version=20210330`; // キャッシュ対策でパラメータ追加
  const res = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
  });
  const json = (await res.json()) as TagsResponse;
  return json;
}

export async function upload(file: File) {
  const url = `${BASE_URL}/contract/document/upload`;
  const data = new FormData();
  data.append('uploadFile', file, file.name);
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as UploadResponse;
  return json;
}

export async function login(usernameVal: string, passwordVal: string) {
  const url = `${BASE_URL}/${AUTH_API}`;
  const bodyt = `{"username": "${usernameVal}", "password": "${passwordVal}"}`;
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: bodyt,
  });
  const json = (await res.json()) as LoginResponse;
  return json;
}

export async function userinfo() {
  const url = `${BASE_URL}/user?version=20210330`; // キャッシュ対策でパラメータ追加
  const res = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
  });
  const json = (await res.json()) as UserInfoResponse;
  return json;
}

export async function uploadForHighlight(file: File) {
  const url = `${BASE_URL}/contract/document/upload`;
  const data = new FormData();
  data.append('uploadFile', file, file.name);
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as UploadForHighlightResponse;
  return json;
}

export async function uploadForSearch(file: File) {
  const url = `${BASE_URL}/contract/document/upload`;
  const data = new FormData();
  data.append('uploadFile', file, file.name);
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as UploadForSearchResponse;
  return json;
}

export async function uploadForRiskJudge(file: File) {
  const url = `${BASE_URL}/contract/document/upload`;
  const data = new FormData();
  data.append('uploadFile', file, file.name);
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as UploadForRiskJudgeResponse;
  return json;
}

export async function riskJudge(id: number) {
  const url = `${BASE_URL}/contract/document/judge`;
  const data = new FormData();
  data.append('id', id.toString());
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as RiskJudgeResponse;
  return json;
}

export async function comparison(fileIds: number[], fileTypes: string[]) {
  const url = `${BASE_URL}/contract/document/comparison`;
  const data = new FormData();
  fileIds.forEach((id, i) => {
    data.append(`fileId${i + 1}`, id.toString());
    data.append(`fileType${i + 1}`, fileTypes[i]);
  });
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as ComparisonResponse;
  return json;
}

export async function comparisonForHighlight(
  fileIds: number[],
  fileTypes: string[],
) {
  // const url = `${BASE_URL}/contract/document/comparison`;
  const url = `${BASE_URL}/contract/document/revisedcheck`;
  const data = new FormData();
  fileIds.forEach((id, i) => {
    data.append(`fileId${i + 1}`, id.toString());
    data.append(`fileType${i + 1}`, fileTypes[i]);
  });
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as ComparisonForHighlightResponse;
  return json;
}

export async function comparisonForHighlightPut(
  fromData: string,
  toData: string,
) {
  // const url = `${BASE_URL}/contract/document/comparison`;
  const url = `${BASE_URL}/contract/document/revisedcheck`;
  const bodyt =
    `{
      "docIndex": ${Number(fromData.split('_')[0])},
      "from": {
        "tagId": "${fromData.split('_')[1]}",
        "index": ${Number(fromData.split('_')[2])}
        },
      "to": {
        "tagId": "${toData.split('_')[1]}",
        "index": ${Number(toData.split('_')[2])}
        }
    }`;
  const res = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: bodyt,
  });
  const json = (await res.json()) as ComparisonForHighlightResponse;
  return json;
}

export async function comparisonForHighlightSave() {
  const url = `${BASE_URL}/contract/document/revisedcheck/save?version=20210330`; // キャッシュ対策でパラメータ追加
  const res = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
  });
  const json = (await res.json()) as ComparisonForHighlightSaveResponse;
  return json;
}

export async function comparisonSearch(fileIds: number[], fileTypes: string[]) {
  const url = `${BASE_URL}/contract/document/search`;
  const data = new FormData();
  fileIds.forEach((id, i) => {
    data.append(`fileId${i + 1}`, id.toString());
    data.append(`fileType${i + 1}`, fileTypes[i]);
  });
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as ComparisonSearchResponse;
  return json;
}

export async function downloadComparisonResult(
  fileIds: number[],
  fileTypes: string[],
) {
  const url = `${BASE_URL}/contract/document/comparison_tsv`;
  const data = new FormData();
  fileIds.forEach((id, i) => {
    data.append(`fileId${i + 1}`, id.toString());
    data.append(`fileType${i + 1}`, fileTypes[i]);
  });
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  return res;
}

export async function apply(name: string, contents: ApplyContent[]) {
  const url = `${BASE_URL}/contract/errorSenetence`;
  const jsonArr: { [key: string]: { [key: string]: string }[] } = {
    sentences: [],
  };
  contents.forEach((content, i) => {
    jsonArr['sentences'].push({
      name,
      orgTagId: content.orgTagId.toString(),
      newTagId: content.newTagId.toString(),
      fileName: content.fileName,
      sentence: content.text,
    });
  });
  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: JSON.stringify(jsonArr),
  });
  const json = (await res.json()) as ApplyResponse;
  return json;
}

export async function search(conditions: SearchConditions) {
  const url = `${BASE_URL}/contract/errorSenetence/search`;
  const data = new FormData();
  if (conditions.name) {
    data.append('name', conditions.name);
  }
  if (conditions.orgTagId) {
    data.append('orgTagId', conditions.orgTagId.toString());
  }
  if (conditions.newTagId) {
    data.append('newTagId', conditions.newTagId.toString());
  }
  if (conditions.status) {
    data.append('status', conditions.status.toString());
  }
  if (conditions.limit !== undefined) {
    data.append('limit', conditions.limit.toString());
  }
  if (conditions.offset !== undefined) {
    data.append('offset', conditions.offset.toString());
  }

  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    body: data,
    headers: {
      Authorization: `JWT ${getAccessToken()}`,
    },
  });
  const json = (await res.json()) as SearchResponse;
  return json;
}

export async function downloadSearchResult(conditions: DownloadConditions) {
  const url = `${BASE_URL}/contract/errorSenetence/tsv`;
  const data = new FormData();
  if (conditions.name) {
    data.append('name', conditions.name);
  }
  if (conditions.orgTagId) {
    data.append('orgTagId', conditions.orgTagId.toString());
  }
  if (conditions.newTagId) {
    data.append('newTagId', conditions.newTagId.toString());
  }
  if (conditions.status) {
    data.append('status', conditions.status.toString());
  }

  const res = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    body: data,
    headers: {
      Authorization: `JWT ${getAccessToken()}`,
    },
  });
  return res;
}

export async function updateErrorSenetence(
  id: string,
  newTagId: number,
  status: number,
) {
  const url = `${BASE_URL}/contract/errorSenetence/${id}`;
  const data = new FormData();
  data.append('newTagId', newTagId.toString());
  data.append('status', status.toString());
  const res = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
    body: data,
  });
  const json = (await res.json()) as UpdateResponse;
  return json;
}

export async function deleteErrorSenetence(id: string) {
  const url = `${BASE_URL}/contract/errorSenetence/${id}`;
  const res = await fetch(url, {
    method: 'DELETE',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      Authorization: `JWT ${getAccessToken()}`,
    },
  });
  const json = (await res.json()) as DeleteResponse;
  return json;
}



// WEBPACK FOOTER //
// ./src/services/contractApi.ts